import React, { ComponentState, FC, useCallback, useEffect, useMemo, useState } from "react";
import Toolbar from "composants/datatable/Toolbar";
import { Field } from "composants/form";
import { tw } from "twind";
import { Fa } from "composants/Icon";
import { FilterBarDefinition } from "types/Component";
import { DatatableFocusState } from "types/Focus";
import { Trans, useTranslation } from "react-i18next";
import { ProcessusProvider } from "composants/processus/ProcessusProvider";
import FilterBarComponent from "composants/datatable/FilterBarComponent";
import { Menu } from "composants/DropDown/Menu";
import SatelliteMenu from "composants/satellite/SatelliteMenu";
import { track } from "tracking";
import { Button, NotificationGroup } from "@axin-org/comet";
import { URL_DOWNLOAD } from "customGlobal";
import auth from "auth";
import DragNDropContext from "composants/DragNDropContext";
import SelectFieldsModal from "containers/SelectFieldsModal/SelectFieldsModal";
import classNames from "classnames";
import { FilterBar } from "types/Search";
import BreakRowDialog from "composants/datatable/dialog/BreakRowDialog";
import { Pojo } from "types/Galaxy";
import toaster from "composants/toaster/toaster";
import { uuidv4 } from "utils/uuid.utils";
import * as api from "api/datatable";
import { AxiosPromise } from "axios";
import Checkbox from "composants/checkbox/Checkbox";
import { Tooltip } from "@axin-org/comet";

type ExportType = "XLS" | "PDF";
export interface ExportProps {
  available: Pojo[];
  chosen: Pojo[];
  type: ExportType;
  showExport: boolean;
}
async function prepareExportData(
  type: ExportType,
  columns: ComponentState[],
  errorTitle: string,
  sjmoCode: string,
  tableName: string,
  filter: string,
  useTrad: boolean
) {
  if (columns.length === 0) {
    toaster.notify({
      id: uuidv4(),
      group: NotificationGroup.DEFAULT,
      title: errorTitle, //t("commun_au_moins_1_champs_export"),
      priority: "NORMAL",
      intent: "DANGER",
      createdAt: new Date().toISOString()
    });
    return;
  }
  // L'export attend une liste de componentState avec un minimum d'informations.
  const chosenComponents: ComponentState[] = columns.map((pojo, index) => {
    return {
      column: pojo.column as string,
      label: useTrad ? (pojo.label as string) : pojo.column,
      typeCompo: pojo.typeCompo as string,
      position: index
    } as ComponentState;
  });
  if (type === "XLS") {
    track("datatable::export", { type: "xls" });
    await exportData(api.callExportXls, chosenComponents, sjmoCode, tableName, filter);
  } else {
    track("datatable::export", { type: "pdf" });
    await exportData(api.callExportPdf, chosenComponents, sjmoCode, tableName, filter);
  }
}

async function exportData(
  callExportApi: (
    sjmoCode: string,
    tableName: string,
    filter: string,
    columns: ComponentState[]
  ) => AxiosPromise<string>,
  chosenComponents: ComponentState[],
  sjmoCode: string,
  tableName: string,
  filter: string
) {
  try {
    const res = await callExportApi(sjmoCode, tableName, filter, chosenComponents);
    let fakeLink = document.createElement("a");
    const host = URL_DOWNLOAD();
    const uri = res.data;

    fakeLink.href = encodeURI(`${host}${uri}${auth.token ? "&access_token=" + auth.token : ""}`);

    fakeLink.click();
  } catch (e) {
    console.log(e);
    console.error(
      "cannot export data for the ",
      tableName,
      "on the module",
      sjmoCode,
      " with the filter '",
      filter,
      "'"
    );
  }
}

interface DatatableToolbarProps {
  sjmoCode: string;
  tableName: string;
  ctrlkey: string;
  focus: DatatableFocusState[];
  selectedFocus: string | null;
  selectedRows: Pojo[];
  filterable: boolean;
  filters: FilterBarDefinition[];
  filterBar: FilterBar | undefined;
  currentFilter: string | undefined;
  lastFilterFetched: string | undefined;
  columnFilter: string[];
  columns: ComponentState[];
  gridPadding: "small" | "large";
  breakRows: string[];
  isDirty: boolean;
  selectAll: boolean;
  rowCount: number;
  selectedNumber: number;
  refresh: () => void;
  onFilterChange: (filter: {
    filterBarId: string | null;
    startDate: string | null;
    endDate: string | null;
    filterBarDefaultDtFilter: string | undefined;
  }) => void;
  onFocusChange: (selected: string) => void;
  onSaveFocusPerso: () => void;
  onRazFocusPerso: () => void;
  onSave?: () => Promise<void>;
  setFilterAllFields: (filterAllFields: string | undefined) => void;
  setGridPadding: (gridPadding: "small" | "large") => void;
  setBreakRows: (breakRows: string[]) => void;
  onDelete?: () => Promise<void>;
  onAdd?: () => void;
  setSelectAll: (selectAll: boolean) => void;
  clearColumnFilter: () => void;
}

const AgTableToolbar: FC<DatatableToolbarProps> = ({
  sjmoCode,
  tableName,
  ctrlkey,
  focus,
  selectedFocus,
  selectedRows,
  filterable,
  filters,
  filterBar,
  currentFilter,
  lastFilterFetched,
  columnFilter,
  columns,
  gridPadding,
  breakRows,
  isDirty,
  selectAll,
  rowCount,
  selectedNumber,
  setFilterAllFields,
  onFilterChange,
  refresh,
  onFocusChange,
  onSaveFocusPerso,
  onRazFocusPerso,
  onSave,
  onDelete,
  setGridPadding,
  setBreakRows,
  onAdd,
  setSelectAll,
  clearColumnFilter
}) => {
  const { t } = useTranslation();
  const [openBreakRowDialog, setOpenBreakRowDialog] = useState(false);

  // Propriétés utilisées pour l'export PDF et XLS
  const [exportProps, setExportProps] = useState<ExportProps>({
    available: [],
    chosen: [],
    type: "XLS",
    showExport: false
  });

  const filterBarDefaultDtFilter = useMemo(() => {
    return focus.find(f => f.focusId === selectedFocus)?.defaultFilterBarDate;
  }, [focus, selectedFocus]);

  const handleFilterChange = useCallback(
    (filterBarId: string | null, startDate: string | null, endDate: string | null) => {
      onFilterChange({ filterBarId, startDate, endDate, filterBarDefaultDtFilter });
    },
    [onFilterChange, filterBarDefaultDtFilter]
  );

  useEffect(() => {
    // on passe les colonnes disponibles pour l'export
    setExportProps({
      available: columns.map(
        c =>
          ({
            id: c.id,
            column: c.column,
            label: c.label,
            typeCompo: c.typeCompo
          } as any)
      ),
      chosen: [],
      type: "XLS",
      showExport: false
    });
  }, [columns]);

  return (
    <>
      <ProcessusProvider
        sjmoCode={sjmoCode}
        tableName={tableName}
        selected={selectedRows}
        dtFilter={lastFilterFetched}
        dtFilterBar={filterBar}
        dtTableName={tableName}
        selectAll={selectAll}
      >
        <Toolbar sjmoCode={sjmoCode} tableName={tableName} tableCtrlKey={ctrlkey}>
          <Toolbar.Left>
            <Checkbox
              label=""
              value={selectAll}
              onValueChange={() => {
                setSelectAll(!selectAll);
              }}
            />
            <span className={tw("ml-2 mr-3 relative inline-block align-middle")}>
              {selectedNumber} / {rowCount}
            </span>
            {selectedRows.length === 0 && !selectAll && (
              <>
                {onAdd && (
                  <Toolbar.Item>
                    <Field>
                      <Toolbar.Add onAdd={onAdd} />
                    </Field>
                  </Toolbar.Item>
                )}
                <Toolbar.Item>
                  <Field addons={true}>
                    <Toolbar.Save
                      title={"Enregistrer"}
                      onSave={onSave}
                      isRounded={false}
                      disabled={onSave === undefined}
                    >
                      <IconSave insertable={true} updatatable={true} isDirty={isDirty} />
                    </Toolbar.Save>

                    <Toolbar.Refresh onRefresh={refresh} />

                    <Toolbar.Satellite
                      renderSatelliteMenu={(
                        isActive: boolean,
                        onClose: () => void,
                        id?: string
                      ) => {
                        return (
                          <SatelliteMenu
                            tableName={tableName}
                            contextId={id}
                            query={currentFilter}
                            columns={columns.slice(0, 4).map(col => col.column)}
                            sjmoCode={sjmoCode}
                            isActive={isActive}
                            onClick={() => {
                              onClose();
                              track("datatable::satellite::menu::open");
                            }}
                            onMouseLeave={onClose}
                            ctrlKey={ctrlkey}
                          />
                        );
                      }}
                    />

                    <Menu autoclose>
                      <Menu.Button
                        className={tw(
                          "px-4 py-2 text-blue-600 bg-white hover:bg-blue-50 flex items-center rounded-r-md"
                        )}
                      >
                        <span>
                          <Trans i18nKey="commun_action" />
                        </span>
                        <Fa icon="caret-down" fixedWidth />
                      </Menu.Button>
                      <Menu.Content>
                        <Menu.Item className="flex">
                          <div className="flex justify-between pr-8">
                            <div
                              className={tw("cursor-pointer")}
                              onClick={() => {
                                setGridPadding("small");
                                track("datatable::gridpadding", { size: "small" });
                              }}
                            >
                              <span
                                className={classNames("icon", {
                                  "has-text-grey": gridPadding !== "small"
                                })}
                                title="small"
                              >
                                <Fa icon="th" aria-label="size grid small" />
                              </span>
                            </div>
                            <div
                              className={tw("cursor-pointer")}
                              onClick={() => {
                                setGridPadding("large");
                                track("datatable::gridpadding", { size: "large" });
                              }}
                            >
                              <span
                                className={classNames("icon", {
                                  "has-text-grey": gridPadding !== "large"
                                })}
                                title="large"
                              >
                                <Fa icon="th-large" aria-label="size grid small" />
                              </span>
                            </div>
                          </div>
                          <div>
                            <Trans i18nKey="commun_grille" />
                          </div>
                        </Menu.Item>
                        <Menu.Separator />
                        <Menu.Item
                          as="a"
                          onClick={() => {
                            setOpenBreakRowDialog(true);
                            track("datatable::breakRow::open");
                          }}
                          aria-label="add break row"
                        >
                          <span className={tw("mr-1")}>
                            <Fa icon="outdent" className={tw("text-blue-600")} fixedWidth />
                          </span>
                          <span>
                            <Trans i18nKey="commun_ajouter_une_rupture" />
                          </span>
                        </Menu.Item>
                        <Menu.Separator />
                        <Menu.Item
                          as="a"
                          onClick={e =>
                            setExportProps({ ...exportProps, type: "XLS", showExport: true })
                          }
                        >
                          <span className={tw("mr-1")}>
                            <Fa icon="file-excel" className={tw("text-blue-600")} fixedWidth />
                          </span>
                          <span>
                            <Trans i18nKey="commun_export_excel" />
                          </span>
                        </Menu.Item>

                        <Menu.Item
                          as="a"
                          onClick={e =>
                            setExportProps({ ...exportProps, type: "PDF", showExport: true })
                          }
                        >
                          <span className={tw("mr-1")}>
                            <Fa icon="file-pdf" className={tw("text-blue-600")} fixedWidth />
                          </span>
                          <span>
                            <Trans i18nKey="commun_export_pdf" />
                          </span>
                        </Menu.Item>
                      </Menu.Content>
                    </Menu>
                  </Field>
                </Toolbar.Item>
              </>
            )}
            {(selectedRows.length > 0 || selectAll) && (
              <Toolbar.Item>
                <Field addons={true}>
                  <Toolbar.Delete
                    onDelete={onDelete}
                    isRounded={false}
                    disabled={onDelete === undefined}
                  />
                  <Toolbar.ProcessMenu disabled={false} />
                </Field>
              </Toolbar.Item>
            )}
            <Toolbar.FocusMenu
              focus={focus}
              selectedFocus={selectedFocus ?? undefined}
              onFocusChange={onFocusChange}
              onSaveFocusPerso={onSaveFocusPerso}
              onRazFocusPerso={onRazFocusPerso}
            />
            {filterable && (
              <div className="flex items-center justify-center">
                <Menu autoclose>
                  <Menu.Button>
                    <Fa icon="search" />
                  </Menu.Button>
                  <Menu.Overlay>
                    <div className="flex flex-col items-start p-5">
                      <FilterBarComponent
                        filters={filters}
                        selectedFilter={filterBar?.filterBarId ?? null}
                        startDate={filterBar?.startDate ?? null}
                        endDate={filterBar?.endDate ?? null}
                        selectedFilterChange={(e: any) =>
                          handleFilterChange(e.target.value, null, null)
                        }
                        startDateChange={(e: any) => handleFilterChange(null, e.target.value, null)}
                        endDateChange={(e: any) => handleFilterChange(null, null, e.target.value)}
                      />
                    </div>
                  </Menu.Overlay>
                </Menu>
                {(filterBar?.filterBarId ||
                  filterBar?.startDate ||
                  filterBar?.endDate ||
                  columnFilter.length > 0) && (
                  <>
                    <span className={tw("ml-3 relative inline-block align-middle")}>
                      <Tooltip
                        label={
                          <ul className="text-xs">
                            {columnFilter.map(c => (
                              <li key={c}>{`- ${c}`}</li>
                            ))}
                          </ul>
                        }
                      >
                        <Button onClick={clearColumnFilter} variant="text" size="sm">
                          <Fa icon={["fas", "filter"]} />
                        </Button>
                      </Tooltip>
                    </span>
                    <span className={tw("ml-2 relative inline-block align-middle")}>
                      {filters.find(f => f.filterBarId === filterBar?.filterBarId)?.label}
                    </span>
                  </>
                )}
                <Toolbar.FilterAllFields
                  onFilterAllFields={(e: React.FocusEvent<HTMLInputElement>) => {
                    setFilterAllFields(e.target.value ?? undefined);
                  }}
                />
              </div>
            )}
          </Toolbar.Left>
        </Toolbar>
      </ProcessusProvider>
      {exportProps.showExport === true && (
        <DragNDropContext>
          <SelectFieldsModal
            title="commun_choisir_champs_a_exporter"
            defaultChosenFields={exportProps.chosen}
            allFields={exportProps.available}
            onValidate={(columns: Pojo[], useTrad: boolean) =>
              prepareExportData(
                exportProps.type,
                columns as any,
                t("commun_au_moins_1_champs_export"),
                sjmoCode,
                tableName,
                currentFilter || "",
                useTrad
              )
            }
            onClose={() => setExportProps({ ...exportProps, showExport: false })}
          />
        </DragNDropContext>
      )}
      {openBreakRowDialog && (
        <BreakRowDialog
          sjmoCode={sjmoCode}
          columns={columns}
          initialBreakRow={breakRows}
          onClose={() => setOpenBreakRowDialog(false)}
          onValidate={newRows => {
            setBreakRows(newRows);
            setOpenBreakRowDialog(false);
          }}
        />
      )}
    </>
  );
};

const IconSave = ({
  insertable,
  updatatable,
  isDirty
}: {
  insertable: boolean;
  updatatable: boolean;
  isDirty: boolean;
}) => {
  const isInsertable = insertable ?? true;
  const isUpdatatable = updatatable ?? true;

  if (isInsertable === false && isUpdatatable === true) {
    return (
      <span className={tw("relative inline-block align-middle w-4 h-4")}>
        <Fa
          className={tw("absolute inset-0 text-center", isDirty ? "text-red-600" : "text-blue-600")}
          icon={["fas", "save"]}
          size="1x"
          transform="grow-3"
        />
        <Fa
          className={tw("absolute inset-0 text-center", "text-black")}
          icon={["fas", "pencil"]}
          size="sm"
          transform="down-9 right-8 shrink-2"
        />
      </span>
    );
  } else if (isInsertable === true && isUpdatatable === false) {
    return (
      <span className="relative inline-block align-middle w-4 h-4">
        <Fa
          className={tw("absolute inset-0 text-center", isDirty ? "text-red-600" : "text-blue-600")}
          icon={["fas", "save"]}
          size="1x"
          transform="grow-3"
        />
        <Fa
          className={tw("absolute inset-0 text-center", "text-black")}
          icon={["fad", "plus-circle"]}
          size="sm"
          transform="down-9 right-8 shrink-2"
          style={
            {
              "--fa-primary-color": "white",
              "--fa-secondary-color": "black"
            } as any
          }
        />
      </span>
    );
  }

  return (
    <span className="w-4 h-4">
      <Fa
        icon={["fas", "save"]}
        className={tw(`h-4 w-4 ${isDirty ? "text-red-600" : "text-blue-600"}`)}
      />
    </span>
  );
};

export default AgTableToolbar;
